import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Camera } from '@liveauctioneers/hammer-ui-core/icons';
import { useEffect, useRef, useState } from 'react';

import { useImageSearch } from '@/hooks/useImageSearch';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useSearchByImageExperiment } from '@/utils/amplitude/experiments/hooks/useSearchByImage.experiment';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { SearchByImageBottomSheet } from './components/SearchByImageBottomSheet';
import { SearchByImagePopover } from './components/SearchByImagePopover';

/**
 * Camera entry point for Search by Image, rendered inside the right of the header
 * search input (as its endAdornment). Mounts for both experiment arms so Amplitude
 * exposure fires; renders nothing for control.
 */
export const SearchByImageEntryPoint = () => {
    const isInTreatment = useSearchByImageExperiment();
    const isMobile = useIsMobileLayout();
    const formatMessage = useAppFormatMessage();
    const { reset } = useImageSearch();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // The mobile bottom sheet portals out of this container and manages its own
        // dismissal (overlay tap / drag / Escape), so the outside-click + Escape
        // handling below is only for the desktop anchored popover.
        if (!isOpen || isMobile) {
            return;
        }
        const handleDocumentClick = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };
        document.addEventListener('click', handleDocumentClick);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, isMobile]);

    if (!isInTreatment) {
        return null;
    }

    return (
        <div
            className="relative flex items-center"
            ref={containerRef}
        >
            <IconButton
                aria-expanded={isOpen}
                aria-haspopup={isMobile ? 'dialog' : 'true'}
                aria-label={String(formatMessage(['visualSearch', 'searchByImage']))}
                hideTooltip
                icon={Camera}
                onClick={() => {
                    // Clear any stale error/results from a prior search before reopening,
                    // so the popover always opens to a clean dropzone.
                    if (!isOpen) {
                        reset();
                    }
                    setIsOpen((open) => !open);
                }}
                size="sm"
                style="neutral"
                type="button"
                variant="transparent"
            />
            {isMobile ? (
                <SearchByImageBottomSheet
                    onClose={() => {
                        setIsOpen(false);
                    }}
                    open={isOpen}
                />
            ) : (
                isOpen && (
                    <SearchByImagePopover
                        onClose={() => {
                            setIsOpen(false);
                        }}
                    />
                )
            )}
        </div>
    );
};
